import { TaskName } from "~/pages/sm/api/interfaces/task";
import {
  EDIT_INCENTIVE,
  EDIT_PARTICIPANT_DATA_UPLOAD,
  EDIT_TISSUE,
  READ_OUTREACH,
  READ_PARTICIPANT,
  READ_PARTICIPANT_TASK,
  READ_SURVEY,
  VIEW_WORKFLOW_EDITOR_PERMISSION,
} from "./permissions";
import { RouteDefinition } from "./useVisibleRoutes";

const { VITE_COST_MANAGEMENT_ENABLED, VITE_SM_INCENTIVES_ENABLED, VITE_SM_TISSUE_TASKS_ENABLED } =
  import.meta.env || process.env;

// Routes beginning with a slash are top-level
export const ROUTE_NOVA_VIEW = "/nova";
export const ROUTE_SM = "/sm";
export const ROUTE_404 = "/404";
export const ROUTE_ADMIN = "/admin";
export const ROUTE_SHARED = "/shared";

// Nova dataset routes (sub-paths under ROUTE_NOVA_VIEW)
export const ROUTE_NOVA_DATASETS = `${ROUTE_NOVA_VIEW}/datasets`;
export const nextGenDatasetDetailPath = (datasetId: string) =>
  `${ROUTE_NOVA_DATASETS}/${datasetId}`;
export const ROUTE_NOVA_PERSONAL_DATASET = `${ROUTE_NOVA_DATASETS}/personal`;

// Nova knowledge base. Mirrors `KB_URL` in analysis_agent/agents/platform_links.py.
export const ROUTE_NOVA_KB = `${ROUTE_NOVA_VIEW}/kb`;

// SM routes
export const ROUTE_PARTICIPANTS = "participants";
export const ROUTE_INCENTIVES = "incentives";
export const ROUTE_UPLOADS = "uploads";
export const ROUTE_SURVEYS = "surveys";
export const ROUTE_OUTREACH_V2 = "outreach-v2";
export const ROUTE_TASKS = "tasks";
export const ROUTE_WORKFLOWS = "workflows";

// Admin routes
export const ROUTE_USERS = "users";
export const ROUTE_GROUPS = "groups";
export const ROUTE_CONVERSATIONS = "conversations";
export const ROUTE_COST_MANAGEMENT = "cost-management";
export const ROUTE_CONVERSATION_DIAGNOSTICS = "conversation-diagnostics";
export const ROUTE_DATASETS = "datasets";
export const ROUTE_TABLES = "tables";

export const FLIPT_ROUTE_KEYS = {
  SM_WORKFLOWS_EDITOR: "sm-workflows-editor",
  SM_CMS: "sm-cms",
  MANIFOLD_NEXTGEN: "manifold-nextgen",
  FORCE_MANIFOLD_NEXTGEN: "force-manifold-nextgen",
  AI_EVALS_DASHBOARD: "ai-evals-dashboard",
  AI_SEARCH_MANAGER: "ai-search-manager",
} as const;

export type FliptFlagKey = (typeof FLIPT_ROUTE_KEYS)[keyof typeof FLIPT_ROUTE_KEYS];

export const ROUTE_DEFINITIONS: {
  [key: string]: RouteDefinition;
} = {
  [`${ROUTE_SM}/${ROUTE_PARTICIPANTS}`]: {
    requiredPermission: READ_PARTICIPANT,
  },
  [`${ROUTE_SM}/${ROUTE_INCENTIVES}`]: {
    requiredFeatureFlag: VITE_SM_INCENTIVES_ENABLED,
    requiredPermission: EDIT_INCENTIVE,
  },
  [`${ROUTE_SM}/${ROUTE_SURVEYS}`]: {
    requiredPermission: READ_SURVEY,
  },
  [`${ROUTE_SM}/${ROUTE_WORKFLOWS}`]: {
    requiredFliptFlag: FLIPT_ROUTE_KEYS.SM_WORKFLOWS_EDITOR,
    requiredPermission: VIEW_WORKFLOW_EDITOR_PERMISSION,
  },
  [`${ROUTE_SM}/${ROUTE_UPLOADS}`]: {
    requiredPermission: EDIT_PARTICIPANT_DATA_UPLOAD,
  },
  [`${ROUTE_SM}/${ROUTE_OUTREACH_V2}`]: {
    requiredPermission: READ_OUTREACH,
  },
  [`${ROUTE_SM}/${ROUTE_TASKS}`]: {
    requiredPermission: READ_PARTICIPANT_TASK,
  },
  [`${ROUTE_SM}/${ROUTE_TASKS}/${TaskName.tissue}`]: {
    requiredFeatureFlag: VITE_SM_TISSUE_TASKS_ENABLED,
    requiredPermission: EDIT_TISSUE,
  },
  [`${ROUTE_ADMIN}/${ROUTE_USERS}`]: {
    requiredPermission: "view:admin-app",
  },
  [`${ROUTE_ADMIN}/${ROUTE_GROUPS}`]: {
    requiredPermission: "view:admin-app",
  },
  [`${ROUTE_ADMIN}/${ROUTE_CONVERSATIONS}`]: {
    requiredPermission: "view:all-ai-conversations",
  },
  [`${ROUTE_ADMIN}/${ROUTE_CONVERSATION_DIAGNOSTICS}`]: {
    requiredPermission: "view:all-ai-conversations",
  },
  [`${ROUTE_ADMIN}/${ROUTE_COST_MANAGEMENT}`]: {
    requiredFeatureFlag: VITE_COST_MANAGEMENT_ENABLED,
    requiredPermission: "view:cost-manager-app",
  },
  [`${ROUTE_ADMIN}/${ROUTE_DATASETS}`]: {
    requiredPermission: "admin:catalog-dataset",
  },
  [`${ROUTE_ADMIN}/${ROUTE_TABLES}`]: {
    requiredPermission: "admin:catalog-table",
  },
};
